import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  CardActions,
  Divider
} from '@mui/material';
import { useCheckout } from '../../contexts/CheckoutContext';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

/**
 * PaymentOptionsStep Component
 * Second step in the checkout process that displays available payment options
 * based on the selected location
 */
const PaymentOptionsStep = () => {
  // Access checkout context
  const {
    customerInfo,
    setCurrentStep,
    setPaymentMethod,
    paymentOptionsData,
    calculateAmount
  } = useCheckout();

  // Get payment options based on customer location
  const getPaymentOptions = () => {
    const { country, province } = customerInfo;

    if (country === 'canada') {
      return paymentOptionsData?.canada?.[province] || {};
    } else if (country === 'usa') {
      return paymentOptionsData?.usa?.default || {};
    } else {
      return paymentOptionsData?.other?.default || {};
    }
  };

  // Get formatted payment amount
  const getFormattedAmount = () => {
    const { country, province, state } = customerInfo;
    const amount = calculateAmount(country, province || state);
    
    if (country === 'canada') {
      // For Canadian provinces, display CAD with tax included
      const formattedAmount = new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'CAD' 
      }).format(amount);
      
      return `${formattedAmount} (Tax Included)`;
    } else {
      // For USA and Other, display both CAD and USD
      const cadAmount = new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'CAD' 
      }).format(amount);
      
      // Calculate approximate USD amount (using approximate exchange rate)
      const usdAmount = new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD' 
      }).format(amount * 0.73); // Using approximate CAD to USD conversion
      
      return (
        <>
          {cadAmount} (Tax Included)<br />
          <span style={{ fontSize: '0.9em', color: '#666' }}>≈ {usdAmount} USD</span>
        </>
      );
    }
  };

  // Handle back button
  const handleBack = () => {
    setCurrentStep(0);
  };

  // Handle credit card payment
  const handleCreditCardPayment = () => {
    const options = getPaymentOptions();
    
    // Set payment method in context
    setPaymentMethod('creditCard');
    
    // Open credit card payment URL in new tab
    if (options.creditCard) {
      window.open(options.creditCard, '_blank');
    }
  };

  // Handle bank transfer option
  const handleBankTransfer = () => {
    setPaymentMethod('bankTransfer');
    setCurrentStep(2);
  };

  // Handle interac e-transfer option (Canada only)
  const handleETransfer = () => {
    setPaymentMethod('interac');
    setCurrentStep(2);
  };

  // Handle wire transfer option (International only)
  const handleWireTransfer = () => {
    setPaymentMethod('wireTransfer');
    setCurrentStep(2);
  };

  const amount = getFormattedAmount();
  const { country } = customerInfo;

  // Common styles to ensure all cards and buttons have consistent appearance
  const commonCardStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    width: { xs: '100%', sm: '320px' }
  };
  
  // Common style for card content to ensure consistent height
  const cardContentStyle = { 
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '200px' // Fixed height for card content
  };

  return (
    <Container maxWidth="xl" sx={{ mx: 'auto' }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 4 }, 
          borderRadius: 2
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          Payment Options
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom sx={{ mb: 3 }}>
          Total Payment: <strong>{amount}</strong>
        </Typography>
        
        <Grid 
          container 
          spacing={1} 
          justifyContent="space-evenly"
          alignItems="stretch"
          sx={{ mb: 3 }}
        >
          {/* Credit Card Option */}
          <Grid 
            item 
            xs={12} 
            sm={country === 'canada' ? 12 : 6} 
            md={country === 'canada' ? 4 : 5}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <Card variant="outlined" sx={commonCardStyle}>
              <CardContent sx={cardContentStyle}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <CreditCardIcon sx={{ mr: 1, flexShrink: 0 }} />
                  <Typography variant="h6" component="h3" noWrap={false}>
                    Credit Card Payment
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <img 
                    src="/assets/images/payment-icons/visa.png" 
                    alt="Visa" 
                    style={{ height: '30px', margin: '0 5px' }}
                  />
                  <img 
                    src="/assets/images/payment-icons/mastercard.png" 
                    alt="Mastercard" 
                    style={{ height: '30px', margin: '0 5px' }}
                  />
                  <img 
                    src="/assets/images/payment-icons/americanexpress.png" 
                    alt="American Express" 
                    style={{ height: '30px', margin: '0 5px' }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Secure online payment via credit card
                </Typography>
              </CardContent>
              <Divider />
              <CardActions>
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth
                  onClick={handleCreditCardPayment}
                >
                  Pay with Credit Card
                </Button>
              </CardActions>
            </Card>
          </Grid>
          
          {/* Bank Transfer Option */}
          <Grid 
            item 
            xs={12} 
            sm={country === 'canada' ? 12 : 6} 
            md={country === 'canada' ? 4 : 5}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <Card variant="outlined" sx={{
                ...commonCardStyle,
                position: 'relative',
                border: country !== 'canada' ? '2px solid #4caf50' : undefined
              }}>
              <CardContent sx={cardContentStyle}>
                {/* Preferred payment badge - only for USA and International */}
                {country !== 'canada' && (
                  <Box 
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 1
                    }}
                  >
                    <Box 
                      sx={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      Preferred
                    </Box>
                  </Box>
                )}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <AccountBalanceIcon sx={{ mr: 1, flexShrink: 0, color: country !== 'canada' ? '#4caf50' : undefined }} />
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    noWrap={false}
                    sx={{ 
                      color: country !== 'canada' ? '#4caf50' : undefined,
                      fontWeight: country !== 'canada' ? 'bold' : undefined
                    }}
                  >
                    {country === 'other' ? 'Wire Transfer' : 'Bank Transfer'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {country === 'other' 
                    ? 'Transfer the payment directly to our international bank account' 
                    : `Transfer the payment directly to our ${country === 'usa' ? 'US' : 'Canadian'} bank account`}
                </Typography>
              </CardContent>
              <Divider />
              <CardActions>
                <Button 
                  variant="contained" 
                  sx={country !== 'canada' ? { 
                    bgcolor: '#4caf50', 
                    '&:hover': { bgcolor: '#3d8b40' } 
                  } : undefined}
                  color={country === 'canada' ? "primary" : undefined}
                  fullWidth
                  onClick={country === 'other' ? handleWireTransfer : handleBankTransfer}
                >
                  Continue with {country === 'other' ? 'Wire Transfer' : 'Bank Transfer'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
          
          {/* E-Transfer Option (Canada only) */}
          {country === 'canada' && (
            <Grid 
              item 
              xs={12} 
              sm={12} 
              md={4}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Card variant="outlined" sx={{
                ...commonCardStyle,
                position: 'relative',
                border: '2px solid #4caf50'
              }}>
                <CardContent sx={cardContentStyle}>
                  {/* Preferred payment badge */}
                  <Box 
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 1
                    }}
                  >
                    <Box 
                      sx={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      Preferred
                    </Box>
                  </Box>
                  {/* Title below badge */}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <CompareArrowsIcon sx={{ mr: 1, color: '#4caf50', flexShrink: 0 }} />
                    <Typography variant="h6" component="h3" sx={{ color: '#4caf50', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      Interac E-Transfer
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Send an Interac E-Transfer to us (our preferred payment method)
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions>
                  <Button 
                    variant="contained" 
                    color="primary"
                    fullWidth
                    onClick={handleETransfer}
                  >
                    Continue with E-Transfer
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          )}
        </Grid>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start' }}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleBack}
          >
            Back
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentOptionsStep;